export interface Mission {
  id: string;
  name: string;
  description: string;
  type: 'daily' | 'weekly' | 'one_time';
  points_reward: number;
  xp_reward?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlayerMission {
  id: string;
  player_id: string;
  mission_id: string;
  completed_at: string;
  points_earned: number;
  mission?: Mission;
}

export interface FriendInvitation {
  id: string;
  inviter_id: string;
  invited_email: string;
  is_registered: boolean;
  created_at: string;
  registered_at?: string;
}


// Daily missions configuration
export const DAILY_MISSIONS = [
  {
    name: 'daily_login',
    description: 'Log in daily',
    points_reward: 2,
    xp_reward: 10,
    action: 'login'
  },
  {
    name: 'like_game',
    description: 'Like a game',
    points_reward: 2,
    xp_reward: 10,
    action: 'like_game'
  },
  {
    name: 'like_developer',
    description: 'Like a developer',
    points_reward: 2,
    xp_reward: 10,
    action: 'like_developer'
  },
  {
    name: 'add_favorite',
    description: 'Add a game to favorites',
    points_reward: 2,
    xp_reward: 10,
    action: 'add_favorite'
  },
  {
    name: 'visit_game_page',
    description: 'Visit a game page',
    points_reward: 2,
    xp_reward: 10,
    action: 'visit_game_page'
  }
] as const;

// Weekly missions configuration
export const WEEKLY_MISSIONS = [
  {
    name: 'weekly_playtime',
    description: 'Play purchased games',
    points_reward: 50,
    xp_reward: 100,
    action: 'play_games'
  }
] as const;

// Configuration constants for missions
export const WEEKLY_PLAYTIME_MISSION = {
  REQUIRED_PLAYTIME_MINUTES: 300, // 5 hours
  PURCHASE_WINDOW_DAYS: 7,
  COOLDOWN_DAYS: 7
} as const;

// One-time missions configuration
export const ONE_TIME_MISSIONS = [
  {
    name: 'first_signup',
    description: 'Sign up to the platform',
    points_reward: 25,
    xp_reward: 150,
    action: 'signup'
  },
  {
    name: 'invite_friend',
    description: 'Invite friends (max 5)',
    points_reward: 20,
    xp_reward: 150,
    action: 'invite_friend'
  }
] as const;


// Player levels configuration
export const PLAYER_LEVELS = {
  bronze: {
    name: 'Bronze',
    min_trust_score: 0,
    max_trust_score: 19,
    color: '#CD7F32',
    description: 'New player'
  },
  silver: {
    name: 'Silver',
    min_trust_score: 20,
    max_trust_score: 39,
    color: '#C0C0C0',
    description: 'Experienced player'
  },
  gold: {
    name: 'Gold',
    min_trust_score: 40,
    max_trust_score: 59,
    color: '#FFD700',
    description: 'Trusted player'
  },
  platinum: {
    name: 'Platinum',
    min_trust_score: 60,
    max_trust_score: 79,
    color: '#E5E4E2',
    description: 'Elite player'
  },
  diamond: {
    name: 'Diamond',
    min_trust_score: 80,
    max_trust_score: 94,
    color: '#B9F2FF',
    description: 'Expert player'
  },
  hero: {
    name: 'Hero',
    min_trust_score: 95,
    max_trust_score: 100,
    color: '#FF6B6B',
    description: 'Legendary player'
  }
} as const;

// Badges configuration with tiers
export const BADGE_TIERS = {
  reviewer: [
    {
      name: 'Commentator',
      description: 'Liked 15 games',
      icon: '💬',
      requirement: { likes_count: 15 }
    },
    {
      name: 'Critic',
      description: 'Liked 50 games',
      icon: '📝',
      requirement: { likes_count: 50 }
    },
    {
      name: 'Reviewer',
      description: 'Liked 150 games',
      icon: '📚',
      requirement: { likes_count: 150 }
    }
  ],
  game_tester: [
    {
      name: 'Casual Tester',
      description: 'Tested 15 games',
      icon: '🎮',
      requirement: { games_tested: 15 }
    },
    {
      name: 'Dedicated Tester',
      description: 'Tested 30 games',
      icon: '🎯',
      requirement: { games_tested: 30 }
    },
    {
      name: 'Game Tester',
      description: 'Tested 50 games',
      icon: '🏆',
      requirement: { games_tested: 50 }
    }
  ],
  loyal_player: [
    {
      name: 'New Member',
      description: 'Been here 30 days',
      icon: '🌱',
      requirement: { days_since_signup: 30 }
    },
    {
      name: 'Veteran',
      description: 'Been here 90 days',
      icon: '⭐',
      requirement: { days_since_signup: 90 }
    },
    {
      name: 'Loyal Player',
      description: 'Been here 365 days',
      icon: '💎',
      requirement: { days_since_signup: 365 }
    }
  ],
  speed_runner: [
    {
      name: 'Rookie Gamer',
      description: 'Played more than 50 hours',
      icon: '🎮',
      requirement: { total_playtime: 3000 } // 50 hours in minutes
    },
    {
      name: 'Ongoing Gamer',
      description: 'Played more than 150 hours',
      icon: '⚡',
      requirement: { total_playtime: 9000 } // 150 hours in minutes
    },
    {
      name: 'Hardcore Gamer',
      description: 'Played more than 500 hours',
      icon: '🔥',
      requirement: { total_playtime: 30000 } // 500 hours in minutes
    }
  ],
  social_butterfly: [
    {
      name: 'Social Starter',
      description: 'Achieved 10 missions',
      icon: '🌱',
      requirement: { social_actions: 10 }
    },
    {
      name: 'Community Member',
      description: 'Achieved 50 missions',
      icon: '🦋',
      requirement: { social_actions: 50 }
    },
    {
      name: 'Social Butterfly',
      description: 'Achieved 150 missions',
      icon: '🦅',
      requirement: { social_actions: 150 }
    }
  ],
  completionist: [
    {
      name: 'First Completion',
      description: 'Completed 1 game 100%',
      icon: '🏁',
      requirement: { games_completed_100: 1 }
    },
    {
      name: 'Dedicated Player',
      description: 'Completed 5 games 100%',
      icon: '🎯',
      requirement: { games_completed_100: 5 }
    },
    {
      name: 'Master Completionist',
      description: 'Completed 15 games 100%',
      icon: '🏆',
      requirement: { games_completed_100: 15 }
    }
  ]
} as const;

// Legacy BADGES for backward compatibility (returns highest tier)
export const BADGES = {
  reviewer: {
    name: 'Reviewer',
    description: 'Liked 50 games',
    icon: '📚',
    requirement: 'likes_count >= 50'
  },
  game_tester: {
    name: 'Game Tester',
    description: 'Tested 50 games',
    icon: '🏆',
    requirement: 'games_tested >= 50'
  },
  loyal_player: {
    name: 'Loyal Player',
    description: 'Been here 365 days',
    icon: '💎',
    requirement: 'days_since_signup >= 365'
  },
  speed_runner: {
    name: 'Hardcore Gamer',
    description: 'Played more than 500 hours',
    icon: '🔥',
    requirement: 'total_playtime_hours >= 500'
  },
  social_butterfly: {
    name: 'Social Butterfly',
    description: 'Achieved 50 missions',
    icon: '🦅',
    requirement: 'social_actions >= 50'
  },
  completionist: {
    name: 'Master Completionist',
    description: 'Completed 10 games 100%',
    icon: '🏆',
    requirement: 'games_completed_100 >= 10'
  }
} as const;

// Function to get player level
export function getPlayerLevel(trustScore: number): keyof typeof PLAYER_LEVELS {
  if (trustScore >= 95) return 'hero';
  if (trustScore >= 80) return 'diamond';
  if (trustScore >= 60) return 'platinum';
  if (trustScore >= 40) return 'gold';
  if (trustScore >= 20) return 'silver';
  return 'bronze';
}

// Function to calculate player badges (returns all tiers achieved for each badge type)
export function calculatePlayerBadges(stats: {
  games_tested: number;
  total_playtime: number; // en minutes
  days_since_signup: number;
  avg_playtime_ratio: number;
  social_actions: number;
  likes_count?: number;
  games_completed_100?: number;
}): string[] {
  const badges: string[] = [];

  // Helper function to get all tiers achieved for a badge category
  function getAllTiersAchieved(
    categoryTiers: typeof BADGE_TIERS[keyof typeof BADGE_TIERS],
    stats: typeof calculatePlayerBadges.arguments[0]
  ): string[] {
    const earned = [];
    for (let i = 0; i < categoryTiers.length; i++) {
      const tier = categoryTiers[i];
      const requirement = tier.requirement;
      let isAchieved = false;

      if ('likes_count' in requirement && requirement.likes_count !== undefined) {
        if (stats.likes_count && stats.likes_count >= requirement.likes_count) {
          isAchieved = true;
        }
      } else if ('games_tested' in requirement && requirement.games_tested !== undefined) {
        if (stats.games_tested >= requirement.games_tested) {
          isAchieved = true;
        }
      } else if ('days_since_signup' in requirement && requirement.days_since_signup !== undefined) {
        if (stats.days_since_signup >= requirement.days_since_signup) {
          isAchieved = true;
        }
      } else if ('total_playtime' in requirement && requirement.total_playtime !== undefined) {
        if (stats.total_playtime >= requirement.total_playtime) {
          isAchieved = true;
        }
      } else if ('social_actions' in requirement && requirement.social_actions !== undefined) {
        if (stats.social_actions >= requirement.social_actions) {
          isAchieved = true;
        }
      } else if ('games_completed_100' in requirement && requirement.games_completed_100 !== undefined) {
        if (stats.games_completed_100 && stats.games_completed_100 >= requirement.games_completed_100) {
          isAchieved = true;
        }
      }

      if (isAchieved) {
        earned.push(tier.name);
      }
    }
    return earned;
  }

  // Get all achieved tiers for each badge category
  const reviewerBadges = getAllTiersAchieved(BADGE_TIERS.reviewer, stats);
  badges.push(...reviewerBadges);

  const testerBadges = getAllTiersAchieved(BADGE_TIERS.game_tester, stats);
  badges.push(...testerBadges);

  const loyalBadges = getAllTiersAchieved(BADGE_TIERS.loyal_player, stats);
  badges.push(...loyalBadges);

  const gamerBadges = getAllTiersAchieved(BADGE_TIERS.speed_runner, stats);
  badges.push(...gamerBadges);

  const socialBadges = getAllTiersAchieved(BADGE_TIERS.social_butterfly, stats);
  badges.push(...socialBadges);

  const completionistBadges = getAllTiersAchieved(BADGE_TIERS.completionist, stats);
  badges.push(...completionistBadges);

  return badges;
}

// Creator badges configuration with tiers
export const CREATOR_BADGE_TIERS = {
  key_collector: [
    {
      name: 'First Key',
      description: 'Received 1 key',
      icon: '🔐',
      requirement: { keys_received: 1 }
    },
    {
      name: 'Key Collector',
      description: 'Received 15 keys',
      icon: '🔑',
      requirement: { keys_received: 15 }
    },
    {
      name: 'Key Master',
      description: 'Received 30 keys',
      icon: '🗝️',
      requirement: { keys_received: 30 }
    }
  ],
  content_creator: [
    {
      name: 'First Video',
      description: 'Created 1 content',
      icon: '🎬',
      requirement: { content_created: 1 }
    },
    {
      name: 'Content Creator',
      description: 'Created 20 contents',
      icon: '📹',
      requirement: { content_created: 20 }
    },
    {
      name: 'Video Master',
      description: 'Created 50 contents',
      icon: '🎥',
      requirement: { content_created: 50 }
    }
  ],
  game_reviewer: [
    {
      name: 'First Review',
      description: 'Covered 1 game',
      icon: '📝',
      requirement: { games_covered: 1 }
    },
    {
      name: 'Game Reviewer',
      description: 'Covered 15 games',
      icon: '📚',
      requirement: { games_covered: 15 }
    },
    {
      name: 'Review Master',
      description: 'Covered 50 games',
      icon: '🏆',
      requirement: { games_covered: 50 }
    }
  ],
  loyal_creator: [
    {
      name: 'New Creator',
      description: 'Been here 30 days',
      icon: '🌱',
      requirement: { days_since_signup: 30 }
    },
    {
      name: 'Veteran Creator',
      description: 'Been here 90 days',
      icon: '⭐',
      requirement: { days_since_signup: 90 }
    },
    {
      name: 'Loyal Creator',
      description: 'Been here 365 days',
      icon: '💎',
      requirement: { days_since_signup: 365 }
    }
  ],
  dedicated_gamer: [
    {
      name: 'Casual Gamer',
      description: 'Played more than 50 hours',
      icon: '🎮',
      requirement: { total_playtime: 3000 } // 50 hours in minutes
    },
    {
      name: 'Dedicated Gamer',
      description: 'Played more than 150 hours',
      icon: '⚡',
      requirement: { total_playtime: 9000 } // 150 hours in minutes
    },
    {
      name: 'Hardcore Creator',
      description: 'Played more than 500 hours',
      icon: '🔥',
      requirement: { total_playtime: 30000 } // 500 hours in minutes
    }
  ],
  active_requester: [
    {
      name: 'First Request',
      description: 'Made 5 requests',
      icon: '📋',
      requirement: { requests_made: 5 }
    },
    {
      name: 'Active Requester',
      description: 'Made 50 requests',
      icon: '📊',
      requirement: { requests_made: 50 }
    },
    {
      name: 'Request Master',
      description: 'Made 200 requests',
      icon: '🎯',
      requirement: { requests_made: 200 }
    }
  ]
} as const;

// Function to calculate creator badges (returns all tiers achieved for each badge type)
export function calculateCreatorBadges(stats: {
  keys_received: number;
  content_created: number;
  games_covered: number;
  days_since_signup: number;
  total_playtime: number; // en minutes
  requests_made: number;
}): string[] {
  const badges: string[] = [];

  // Helper function to get all tiers achieved for a badge category
  function getAllTiersAchieved(
    categoryTiers: typeof CREATOR_BADGE_TIERS[keyof typeof CREATOR_BADGE_TIERS],
    stats: typeof calculateCreatorBadges.arguments[0]
  ): string[] {
    const earned = [];
    for (let i = 0; i < categoryTiers.length; i++) {
      const tier = categoryTiers[i];
      const requirement = tier.requirement;
      let isAchieved = false;

      if ('keys_received' in requirement && requirement.keys_received !== undefined) {
        if (stats.keys_received >= requirement.keys_received) {
          isAchieved = true;
        }
      } else if ('content_created' in requirement && requirement.content_created !== undefined) {
        if (stats.content_created >= requirement.content_created) {
          isAchieved = true;
        }
      } else if ('games_covered' in requirement && requirement.games_covered !== undefined) {
        if (stats.games_covered >= requirement.games_covered) {
          isAchieved = true;
        }
      } else if ('days_since_signup' in requirement && requirement.days_since_signup !== undefined) {
        if (stats.days_since_signup >= requirement.days_since_signup) {
          isAchieved = true;
        }
      } else if ('total_playtime' in requirement && requirement.total_playtime !== undefined) {
        if (stats.total_playtime >= requirement.total_playtime) {
          isAchieved = true;
        }
      } else if ('requests_made' in requirement && requirement.requests_made !== undefined) {
        if (stats.requests_made >= requirement.requests_made) {
          isAchieved = true;
        }
      }

      if (isAchieved) {
        earned.push(tier.name);
      }
    }
    return earned;
  }

  // Get all achieved tiers for each badge category
  const keyCollectorBadges = getAllTiersAchieved(CREATOR_BADGE_TIERS.key_collector, stats);
  badges.push(...keyCollectorBadges);

  const contentCreatorBadges = getAllTiersAchieved(CREATOR_BADGE_TIERS.content_creator, stats);
  badges.push(...contentCreatorBadges);

  const reviewerBadges = getAllTiersAchieved(CREATOR_BADGE_TIERS.game_reviewer, stats);
  badges.push(...reviewerBadges);

  const loyalBadges = getAllTiersAchieved(CREATOR_BADGE_TIERS.loyal_creator, stats);
  badges.push(...loyalBadges);

  const gamerBadges = getAllTiersAchieved(CREATOR_BADGE_TIERS.dedicated_gamer, stats);
  badges.push(...gamerBadges);

  const requesterBadges = getAllTiersAchieved(CREATOR_BADGE_TIERS.active_requester, stats);
  badges.push(...requesterBadges);

  return badges;
}

// Function to get 3 random daily missions
export function getRandomDailyMissions(): typeof DAILY_MISSIONS[number][] {
  const shuffled = [...DAILY_MISSIONS].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, 3);
}
